import { FINISH_OPTION_VALUES, type FinishOptionKey, type FinishOptions } from './finishes.ts';
import { layoutSpec, type LayoutOptionKey } from './layouts.ts';

/**
 * O que o Estúdio de estilos oferece (ADR 0128, fase 5): só os ajustes que a base já desenha.
 * Os outros existem no modelo (o protótipo e o Smart Builder podem gravá-los), mas não aparecem
 * para escolha até alguma tela os usar, senão o cliente escolhe algo que o sistema não mostra.
 * Um teste da web confere que cada ajuste daqui é lido pelo código das telas.
 */
export const STUDIO_LAYOUT_OPTIONS: readonly LayoutOptionKey[] = [
  'submenu',
  'navLabels',
  'header',
  'filters',
  'kpi',
  'recordOpen',
  'pageWidth',
  'subnav',
  'listWidth',
  'statusBar',
  'tiles',
  'contentWidth',
  'topLinks',
  'ribbon',
  'rowNumbers',
  'density',
];

export const STUDIO_FINISH_OPTIONS: readonly FinishOptionKey[] = Object.keys(
  FINISH_OPTION_VALUES,
) as FinishOptionKey[];

/** Os ajustes do acabamento em grupos, como o protótipo os mostra (ADR 0133). */
export const STUDIO_FINISH_GROUPS: readonly { id: string; options: readonly FinishOptionKey[] }[] =
  [
    {
      id: 'type',
      options: ['font', 'headFont', 'textSize', 'headWeight', 'tracking', 'labelCase', 'numFont'],
    },
    { id: 'shape', options: ['radius', 'btnShape', 'panel', 'activeMark'] },
    { id: 'icons', options: ['iconStyle', 'iconStroke', 'iconSize', 'iconTint'] },
    { id: 'controls', options: ['btnPrimary', 'btnSecondary', 'btnIcon', 'input', 'labelPos'] },
    { id: 'status', options: ['badge', 'tabs', 'alert'] },
    {
      id: 'tables',
      options: ['tableHeader', 'tableRows', 'tableBorder', 'rowActions', 'headerRule'],
    },
    { id: 'charts', options: ['chartStyle', 'avatar'] },
    { id: 'states', options: ['emptyStyle', 'docHeader'] },
  ];

/** Os valores de um ajuste do acabamento que o estúdio oferece: todos (ADR 0133). */
export function offeredFinishValues<K extends FinishOptionKey>(
  key: K,
): readonly FinishOptions[K][] {
  return FINISH_OPTION_VALUES[key] as unknown as readonly FinishOptions[K][];
}

/** Os ajustes de um layout que o estúdio mostra, na ordem do layout. */
export function offeredLayoutOptions(id: string): LayoutOptionKey[] {
  return (layoutSpec(id)?.options ?? []).filter((k) => STUDIO_LAYOUT_OPTIONS.includes(k));
}

/**
 * Até 5 cores que mais aparecem num logo, para a paleta `logo`. Recebe os pixels RGBA (o que o
 * `canvas` devolve); ignora o transparente e o quase branco (o fundo), junta tons parecidos e
 * devolve da mais frequente para a menos.
 */
export function logoColorsOf(rgba: ArrayLike<number>, max = 5): string[] {
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i + 3 < rgba.length; i += 4) {
    const [r, g, b, a] = [rgba[i]!, rgba[i + 1]!, rgba[i + 2]!, rgba[i + 3]!];
    if (a < 128 || (r > 238 && g > 238 && b > 238)) continue;
    const key = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
    const s = buckets.get(key) ?? { n: 0, r: 0, g: 0, b: 0 };
    s.n++;
    s.r += r;
    s.g += g;
    s.b += b;
    buckets.set(key, s);
  }
  const total = [...buckets.values()].reduce((sum, s) => sum + s.n, 0);
  const hex = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return [...buckets.values()]
    .filter((s) => s.n / total >= 0.02)
    .sort((a, b) => b.n - a.n)
    .slice(0, max)
    .map((s) => `#${hex(s.r / s.n)}${hex(s.g / s.n)}${hex(s.b / s.n)}`);
}

/** O valor como ele é marcado no estúdio (cada valor tem desenho próprio desde o ADR 0133). */
export function drawnFinishValue<K extends FinishOptionKey>(
  _key: K,
  value: FinishOptions[K],
): FinishOptions[K] {
  return value;
}
